import { useEffect, useRef, useState } from 'react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import EditPopup from '../../components/Popup/EditPopup/EditPopup'; 
import AddPopup from '../../components/Popup/AddPopup/AddPopup';
import ArchivePopup from '../../components/Popup/ArchivePopup/ArchivePopup';
import DeletePopup from '../../components/Popup/DeletePopup/DeletePopup';
import EditIcon from '../../components/Table/EditIcon/EditIcon';
import DeleteIcon from '../../components/Table/DeleteIcon/DeleteIcon';
import ArchiveIcon from '../../components/Table/ArchiveIcon/ArchiveIcon';
import ExportLabel from '../../components/common/ExportLabel/Export';
import AddButton from '../../components/common/AddButton/AddButton';
import Filter from '../../components/common/Filter/Filter';
import Sort from '../../components/common/Sort/Sort';
import '../../components/common/SubNavbar.css';
import '../../components/common/CommonText.css';
import SimpleSearchBox from '../../components/common/Search/SimpleSearch';
import { baseurl } from '../../api';
import { isRequired, isTextOnly } from '../../validation';
import { useCheckboxClick } from '../../checkboxclick';
import ArchiveSelectedItem from '../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import authenticatedAxios from '../../authenticatedAxios';
import useFilterStore from '../../store/useFilterStore';
import { useNavigate } from 'react-router-dom';
import useRefreshStore from '../../store/useRefreshStore';
import { fetchFaqCategories } from './redux/faqCategorySlice';
import { addFaqCategory, archiveCategoriesBulk, archiveCategory, deleteCategory, editFaqCategory, getFaqCategories, getFaqCategoryById } from '../../services/Faq/FaqCategoryService';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
const pageKey = "faqcategory"

const FaqCategories = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [isNewPopupOpen, setIsNewPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState([]);
  const [items, setItems] = useState([]);
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const navigate = useNavigate();
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState, } = useCheckboxClick(items);
  const sortOptions = ['A-Z', 'Z-A', 'Newest', 'Oldest'];
  const [popupError, setPopupError] = useState('');
  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["FAQ Categories"] || 0;
  const dispatch = useDispatch();
  const [debouncedSearchTerm] = useDebounce(searchParams.term, 300);


  const fetchCategories = async (id = null) => {
    try {
      if (id) {
        const data = await getFaqCategoryById(id);
        setSelectedItem(data);
        setIsEditPopupOpen(true);
      } else {
        const params = {
          search: searchParams.term,
          filter: selectedFilterOption === "All" ? undefined : selectedFilterOption.toLowerCase(),
          sort: mapSortOptionToQuery(selectedSortOption),
        };

        const data = await getFaqCategories(params);
        setItems(data);
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      } else {
        console.error("Error fetching categories:", error.message);
      }
    }
  };
  

  useEffect(() => {
    fetchCategories();
  }, [debouncedSearchTerm, selectedFilterOption, selectedSortOption, refreshKey]);


  const handleAddCategory = async (formValues) => {
    try {
      await addFaqCategory(formValues); 

      dispatch(fetchFaqCategories());
      await fetchCategories();

      closePopups();
    } catch (error) {
      setPopupError(error.message);
    }
  };
  
  const handleEditCategory = async (formValues) => {
    try {
      await editFaqCategory(selectedItem.id, formValues);

      dispatch(fetchFaqCategories());
      await fetchCategories();

      closePopups();
    } catch (error) {
      setPopupError(error.message);
    }
  };
  
  const handleArchiveSelectedItems = async () => {
    try {
      await archiveCategoriesBulk(selectedItems);
      fetchCategories();
      resetCheckboxState();
      closePopups();
    } catch (_) {
      // Optionally show a toast or popup error here
    }
  };

  const handleArchive = async () => {
    try {
      await archiveCategory(selectedItem.id);
      await fetchCategories();
      closePopups();
    } catch (_) {
      // Optionally show a toast or popup error here
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(selectedItem.id);
      await fetchCategories();
      closePopups();
    } catch (_) {
      // Optionally show a toast or popup error here
    }
  };

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':
        return 'a_to_z';
      case 'Z-A':
        return 'z_to_a';
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption: option, // Use the new value
    });
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption: option, // Use the new value
      selectedSortOption,
    });
  };

  const handleSearchSelect = (searchTerm) => {
    setSearchParams({ term: searchTerm });
    setFilters(pageKey, {
      searchParams: { term: searchTerm }, // Use the new value
      selectedFilterOption,
      selectedSortOption,
    });
  };


  const handleEditClick = (item) => {
    fetchCategories(item.id); 
  };
  
  const handleArchiveClick = async (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };


  const handleNewClick = () => {
    setSelectedItem(null);
    setIsNewPopupOpen(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item); 
    setIsDeletePopupOpen(true); 
  };

  const closePopups = () => {
    setIsEditPopupOpen(false);
    setIsArchivePopupOpen(false);
    setIsNewPopupOpen(false);
    setIsDeletePopupOpen(false);
    setSelectedItem(null);
    setPopupError('');
  };

  const addPopupFields = [
    { label: 'Category Name', 'name':'name',type: 'large-input', defaultValue: '', onChange: () => {}, validators:[isTextOnly,isRequired] }
  ];

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? Once deleted, the data will be permanently removed.', type: 'text' },
  ];

  const editPopupFields = [
    { label: 'Category Name', 'name':'name', type: 'large-input', defaultValue: selectedItem?.name,validators:[isTextOnly,isRequired], onChange: () => {} }
  ];


  const handleExportComplete = () => {
    resetCheckboxState();
  };


  return (
    <div className='whole-container'>
      <div className='top-heading'>
        <PageHeader pageName=" FAQ /" nestedpageName="FAQ Categories"  linkTo="/faqs" /> 
        
        <div className='export-container'>
        <ArchiveSelectedItem
          selectedItems={selectedItems}
          items={items}
          onArchiveSelected={handleArchiveSelectedItems}
        />
          <ExportLabel
            exportUrl={`${baseurl}faq/category/export/`}
            selectedIds={selectedItems}
            onExportComplete={handleExportComplete}
          />
        </div>
      </div>
      <div className='pagename-container'>
        <div className='page-name'>
          <h1 className='common-text fts-22 ftw-6 blue'>FAQ Categories</h1>
        </div>
        <div className='pagename-right-container'>
          <AddButton onClick={handleNewClick} />
          <SimpleSearchBox placeholder="Search FAQ Categories" onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term}/>
          <Filter onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption}/>
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>
      <div className='table-container'>
        <table className='common-table'>
          <thead className='common-table-head'>
            <tr className='common-table-thead-tr'>
              <th>
                  <input
                    className="table-selectbox table-parent-selectbox"
                    type="checkbox"
                    checked={isParentSelected}
                    onChange={handleParentCheckboxChange}
                  />Category
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className='common-table-tbody'>
          {items.length > 0 ? (
            items.map((category) => (
              <tr key={category.id} className={category.is_archived ? 'archived-item' : ''}>
                <td>
                  <input
                    className="table-selectbox table-child-selectbox"
                    type="checkbox"
                    checked={selectedItems.includes(category.id)}
                    onChange={() => handleChildCheckboxChange(category.id)}
                  />{category.name}
                </td>
                <td className="actions common-action">
                  <EditIcon onClick={() => handleEditClick(category)} />
                  <ArchiveIcon onClick={() => handleArchiveClick(category)}  isArchived={category.is_archived} />
                  <DeleteIcon onClick={() => handleDeleteClick(category)} />
                </td>
              </tr>
            ))
          ):(
            <tr>
              <td colSpan="3">No Category Available</td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      <EditPopup 
        isOpen={isEditPopupOpen}
        title="FAQ Category"
        fields={editPopupFields}
        onSave={handleEditCategory}
        onClose={closePopups}
        popupErrorMessage={popupError}
      />
      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived}
        archivename={selectedItem?.name}
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />


      <DeletePopup 
        isOpen={isDeletePopupOpen}
        title="Delete Category"
        fields={deletePopupFields}
        onSave={handleDelete}
        onClose={closePopups}
      />
      <AddPopup 
        isOpen={isNewPopupOpen}
        title="New FAQ Category"
        buttonName='Add FAQ Category'
        fields={addPopupFields}
        onSave={handleAddCategory}
        onClose={closePopups}
        popupErrorMessage={popupError}
      />
    </div>
  );
};

export default FaqCategories;
